import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { DEFAULT_DATE_FORMAT, Module } from "@/constants";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ApiError, SuccessFetchListResponse } from "@/services/api";
import { ReadProjectLimit, WriteProjectLimit } from "@/types/projectLimit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ProjectLimitService } from "../../service/projectLimit";
import { format } from "date-fns";
import { ReadProject } from "@/types/project";
import useQueryData from "@/hooks/useQueryData";
import { useFormBaseline } from "@/hooks/useFormBaseline";

import { ProjectLimitFormSchema } from "./schema";
import { ProjectLimitForm } from "./types";
import { convertFormToWriteProjectLimit } from "./utils/convertFormToWriteProjectLimit";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "../../hooks/useCheckIsProjectLocked";

interface ProjectLimitsContextType {
  period: Date;
  project?: ReadProject;
  data: ReadProjectLimit[];
  handleChangeLimitSubmit: (values: ProjectLimitForm) => Promise<void>;
  form: UseFormReturn<ProjectLimitForm>;
  baseline: ProjectLimitForm;
  isAddLimitsDialogOpen: boolean;
  setIsAddLimitsDialogOpen: (value: boolean) => void;
  inFetching: boolean;
  getItemId: (item: unknown) => string;
}

const ProjectLimitsContext = createContext<
  ProjectLimitsContextType | undefined
>(undefined);

export const ProjectLimitsContextProvider = ({
  children,
  getItemId,
  period,
  project,
}: {
  children: React.ReactNode;
  getItemId: (item: ReadProjectLimit) => string;
  period: Date;
  project?: ReadProject;
}) => {
  const queryClient = useQueryClient();
  const [isAddLimitsDialogOpen, setIsAddLimitsDialogOpen] = useState(false);

  const { t } = useTranslation();
  const { query } =
    useDataTableQuery<SuccessFetchListResponse<ReadProjectLimit>>();
  const projectId = useProjectId();
  const data = useQueryData(query);
  const { can_update } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();

  const defaultValues = useMemo(() => {
    return {
      stores: data.reduce(
        (stores, limit) => {
          if (!stores[limit.store_id]) {
            stores[limit.store_id] = {};
          }
          if (!stores[limit.store_id][getItemId(limit)]) {
            stores[limit.store_id][getItemId(limit)] = {
              id: limit.id.toString(),
              hours: limit.hours.toString(),
              rate: limit.rate.toString(),
              addition: limit.addition.toString(),
            };
          }
          return stores;
        },
        {} as ProjectLimitForm["stores"],
      ),
      period: format(period, DEFAULT_DATE_FORMAT),
    };
  }, [data, getItemId, period]);

  const mutateProjectLimits = async (values: WriteProjectLimit) => {
    await ProjectLimitService.patch(projectId, values);
    return values;
  };

  const projectLimitsMutation = useMutationWithErrorHandling<
    WriteProjectLimit,
    ApiError,
    WriteProjectLimit,
    unknown
  >({
    mutationFn: mutateProjectLimits,
    errorTitle: t("projectLimits.errors.update"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Module.ProjectLimits, projectId],
        refetchType: "all",
      });
      query.refetch();
      setIsAddLimitsDialogOpen(false);
    },
  });

  const form = useForm<ProjectLimitForm>({
    resolver: zodResolver(ProjectLimitFormSchema),
    defaultValues,
    disabled: !can_update || isProjectLocked,
  });

  const { baseline, updateBaseline } = useFormBaseline(form, defaultValues);

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  useEffect(() => {
    form.setValue("period", format(period, DEFAULT_DATE_FORMAT));
  }, [period, form]);

  const handleChangeLimitSubmit = useCallback(
    async (values: ProjectLimitForm) => {
      const dirtyFields = form.formState.dirtyFields;
      const changedValues = convertFormToWriteProjectLimit(values, dirtyFields);
      await projectLimitsMutation.mutateAsync(changedValues);
      updateBaseline();
    },
    [form.formState.dirtyFields, projectLimitsMutation, updateBaseline],
  );

  const inFetching = useMemo(() => {
    return query.isFetching || projectLimitsMutation.isPending;
  }, [query.isFetching, projectLimitsMutation.isPending]);

  const value: ProjectLimitsContextType = {
    period,
    project,
    data,
    form,
    baseline,
    isAddLimitsDialogOpen,
    setIsAddLimitsDialogOpen,
    handleChangeLimitSubmit,
    inFetching,
    getItemId,
  };

  return (
    <ProjectLimitsContext.Provider value={value}>
      {children}
    </ProjectLimitsContext.Provider>
  );
};

export const useProjectLimitsContext = () => {
  const context = useContext(ProjectLimitsContext);
  if (!context) {
    throw new Error(
      "useProjectLimitsContext must be used within a ProjectLimitsContextProvider",
    );
  }
  return context;
};
